import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';

export interface UploadEvent {
  content: UploadContent[];
  reason?: FileCheckType;
}

export interface UploadContent {
  content: File;
  path: string;
  overwrite?: boolean;
}

export interface FileMetaData {
  FileName: string;
  FileType: string;
  Content: string;
  IsNew: boolean;
  IsDeleted: boolean;
  isError: boolean;
  reason?: string;
}

export enum FileCheckType {
  MaxFileSize,
  MinFileSize,
  FileType,
  FileCorrupt,
  FileResolution,
}

interface FileSystemEntry {
  fullPath: string;
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}

interface FileSystemFileEntry extends FileSystemEntry {
  file(successCallback: (file: File) => void, errorCallback?: (error: FileError) => void): void;
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader(): FileSystemDirectoryReader;
  getDirectory(): FileSystemDirectoryEntry;
  getFile(): FileSystemFileEntry;
}

interface FileSystemDirectoryReader {
  readEntries(successCallback: (entries: FileSystemEntry[]) => void, errorCallback?: (error: FileError) => void): void;
}

interface Metadata {
  modificationTime: Date;
  size: number;
}

interface FileError extends Error {}

@Component({
  selector: 'app-browse-filefolder-dragdrop-content',
  templateUrl: './browse-filefolder-dragdrop-content.component.html',
  styleUrls: ['./browse-filefolder-dragdrop-content.component.scss'],
})
export class BrowseFilefolderDragdropContentComponent implements OnInit {
  @ViewChild('fileInput', { static: true })
  public fileUpload: ElementRef;
  @Input()
  public maxFileSize: number = 0;
  @Input()
  public minFileSize: number = 0;
  @Input()
  public fileTypes: string[] = [];
  @Input()
  public disabled: boolean = false;
  @Input()
  public isDragAndDropStyle: boolean = false;
  @Input()
  public restrictFileSelection: boolean = false;
  @Input()
  public customDragAndDropMessage: string;
  @Input()
  public uploadingMessage: string;
  @Input()
  public uploadDescription: boolean = false;
  @Input()
  public readyToUploadState: boolean = false;
  @Input()
  public readFileExtension: boolean = false;
  @Input()
  public iconRequired: boolean = false;
  @Input()
  public refreshFile: boolean = false;
  @Input()
  public showLoader: boolean = false;
  @Input()
  public imageMinimumHeight: number;
  @Input()
  public imageMinimumWidth: number;
  @Input()
  public isFolderUpload: boolean;
  @Input()
  public hasMultipleFilesSupport: boolean;
  @Input()
  public fileTypeIcon: string;
  @Output()
  public uploadEvent: EventEmitter<UploadEvent> = new EventEmitter<UploadEvent>();
  @Output()
  public fileUploadCancelEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  public errorMessageEvent: EventEmitter<string> = new EventEmitter<string>();

  public violated: boolean = false;
  public hasContentSelected: boolean = false;
  public isDragOver: boolean = false;
  public confirmDelete: boolean = false;
  public file: File;
  public dragAndDropIconName: string;
  public fileUploadMessage: string = '';
  public tempFile: File = null;
  public contents: UploadContent[] = [];
  public pipeContent: string;
  public hasMultipleFilesUpload: boolean;
  public countOfFoldersUploaded: number = 0;
  public countOfFilesUploaded: number = 0;
  public iconOfSelectedContent: string = '';
  public hasFolderContentUploaded: boolean = false;

  private uploadElement: HTMLInputElement;
  private selectedContentName: string;
  private uploadContentToolTip: string;
  private errorMessage: string = '';

  constructor(private saI18nPipe: XsI18nPipe) {}

  public ngOnInit(): void {
    this.uploadElement = this.fileUpload.nativeElement;
  }

  public fileCheck(file: File, previewMode: boolean = false) {
    if (!file) {
      return;
    }
    this.tempFile = file;
    let isMinFileSizeValid = this.minFileSize === 0 || file.size >= this.minFileSize;
    let isMaxFileSizeValid = this.maxFileSize === 0 || file.size <= this.maxFileSize;
    let fileType = file.type === '' || this.readFileExtension ? this.getFileExtension(file) : file.type;
    let fileTypeLowered = this.fileTypes.toLocaleString().toLowerCase().split(',');
    let isFileTypeValid =
      !this.fileTypes || this.fileTypes.length === 0 || fileTypeLowered.includes(fileType.toLowerCase());
    if (isMinFileSizeValid && isMaxFileSizeValid && isFileTypeValid) {
      this.updateValidators(false, file, null, previewMode);
    } else if (!isFileTypeValid) {
      this.updateValidators(true, null, FileCheckType.FileType, previewMode);
    } else if (!isMaxFileSizeValid) {
      this.updateValidators(true, null, FileCheckType.MaxFileSize, previewMode);
    } else if (!isMinFileSizeValid) {
      this.updateValidators(true, null, FileCheckType.MinFileSize, previewMode);
    }
  }

  public badFile(event: Event): void {
    this.stopEventBubble(event);
    this.updateValidators(true, null, FileCheckType.FileCorrupt);
  }

  public fileSelected(files: FileList | Event) {
    if (files instanceof Event && files.currentTarget instanceof HTMLInputElement) {
      if ((this.isFolderUpload || this.hasMultipleFilesSupport) && files.currentTarget.files) {
        this.getFiles(Array.from(files.currentTarget.files));
      } else {
        this.fileCheck(files.currentTarget.files[0]);
      }
    } else {
      if (this.hasMultipleFilesSupport) {
        this.getFiles(Array.from(files as FileList));
      } else {
        this.fileCheck(files[0]);
      }
    }
  }

  public getErrorMessage = () => {
    return this.errorMessage;
  };

  public getUploadContentTypeIcon = () => {
    return this.iconOfSelectedContent;
  };

  public openFileExplorer(isFileUpload: boolean): void {
    if (this.disabled) {
      return;
    }
    this.uploadElement.value = null;
    if (!isFileUpload) {
      this.uploadElement.setAttribute('webkitdirectory', '');
      this.uploadElement.setAttribute('mozdirectory', '');
    } else {
      this.uploadElement.removeAttribute('webkitdirectory');
      this.uploadElement.removeAttribute('mozdirectory');
    }

    if (this.hasMultipleFilesSupport) {
      this.uploadElement.setAttribute('multiple', '');
    } else {
      this.uploadElement.removeAttribute('multiple');
    }

    this.uploadElement.click();
  }

  public removeFile(event: Event): void {
    if (this.disabled) {
      return;
    }
    this.tempFile = null;
    this.hasContentSelected = false;
    this.confirmDelete = !this.confirmDelete;
    this.selectedContentName = null;
    this.setErrorMessage('');
    this.contents = [];
    this.countOfFoldersUploaded = 0;
    this.countOfFilesUploaded = 0;
    this.fileCleanUpOperation();
  }

  public fileDropped(event: DragEvent) {
    if (this.disabled) {
      return;
    }
    this.isDragOver = false;
    this.stopEventBubble(event);

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.getContents(event.dataTransfer).then(draggedContent => {
        this.getDraggedContent(draggedContent);
      });
    }
  }

  public dragOver(event: Event): void {
    this.isDragOver = true;
    this.stopEventBubble(event);
  }

  public notDragOver(event: Event): void {
    this.isDragOver = false;
    this.stopEventBubble(event);
  }

  public getAcceptedFileExtensions(): string {
    if (!this.restrictFileSelection || !this.fileTypes || this.fileTypes.length === 0) {
      return null;
    }
    return this.fileTypes.join(',');
  }

  public get hasUploadPlaceholder(): boolean {
    return !this.hasContentSelected;
  }

  public getPlaceholderText(): string {
    if (this.customDragAndDropMessage) {
      return this.customDragAndDropMessage;
    }
    return this.saI18nPipe.transform('file-manager-dragdrop-placeholder');
  }

  public getFilesLinkText(): string {
    if (!this.contents || this.contents.length === 0) {
      return this.saI18nPipe.transform('file-manager-browse-files');
    } else {
      return this.saI18nPipe.transform('file-manager-add-files');
    }
  }

  public getFolderLinkText(): string {
    if (!this.contents || this.contents.length === 0) {
      return this.saI18nPipe.transform('file-manager-browse-folder');
    } else {
      return this.saI18nPipe.transform('file-manager-add-folder');
    }
  }

  public getUploadingMessage = () => {
    if (this.uploadingMessage) {
      return this.uploadingMessage;
    }
    return this.saI18nPipe.transform('file-manager-default-uploading-message');
  };

  public getSelectedContentDescription(): string {
    return this.selectedContentName;
  }

  public getUploadContentToolTipText(): string {
    if (this.uploadContentToolTip !== '') {
      return this.uploadContentToolTip;
    }
  }

  private getContents(dataTransfer: DataTransfer): Promise<UploadContent[]> {
    return new Promise<UploadContent[]>((resolve, reject) => {
      const contents: UploadContent[] = [];
      const promises: Promise<UploadContent[]>[] = [];
      try {
        for (let i = 0; i < dataTransfer.items.length; i++) {
          const item = dataTransfer.items[i];
          if (item.kind === 'file') {
            if (typeof item.webkitGetAsEntry === 'function') {
              const entry = item.webkitGetAsEntry();
              /*
                            var entryDTO = {
                                fullPath: entry.fullPath,
                                isFile: entry.isFile,
                                isDirectory: entry.isDirectory,
                                name: entry.name,
                            } as FileSystemEntry ;*/
              promises.push(this.readEntryContent(entry));
            }
          }
        }
        Promise.all(promises).then(entryContents => {
          for (let content of entryContents) {
            contents.push(...content);
          }
          resolve(contents);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  private readEntryContent(entry: FileSystemEntry): Promise<UploadContent[]> {
    return new Promise<UploadContent[]>((resolve, reject) => {
      let reading = 0;
      const uploadDragContent: UploadContent[] = [];
      const readEntry = (entry: FileSystemEntry, filepath: string) => {
        try {
          if (entry.isFile) {
            reading++;
            (entry as FileSystemFileEntry).file(file => {
              reading--;
              uploadDragContent.push({
                content: file,
                path: filepath,
              });
              if (reading === 0) {
                resolve(uploadDragContent);
              }
            });
          } else if (entry.isDirectory) {
            readReaderContent((entry as FileSystemDirectoryEntry).createReader());
          }
        } catch (error) {
          reject(error);
        }
      };

      const readReaderContent = (reader: FileSystemDirectoryReader) => {
        reading++;
        reader.readEntries(entries => {
          reading--;
          entries.forEach(entry => {
            readEntry(entry, entry.fullPath.slice(1));
          });
          if (reading === 0) {
            resolve(uploadDragContent);
          }
        });
      };

      readEntry(entry, '');
    });
  }

  private getFiles = (files: File[]) => {
    if (files.length == 0) {
      this.setErrorMessage(this.saI18nPipe.transform('file-manager-upload-errormessage-cannot-upload-an-empty-folder'));
      return;
    }
    this.getCountOfFilesOrFolderUploaded(files);
    this.addToContents(files);
    this.contentSelected();
  };

  private getDraggedContent = (content: UploadContent[]) => {
    if (content.length == 0) {
      this.setErrorMessage(this.saI18nPipe.transform('file-manager-upload-errormessage-cannot-upload-an-empty-folder'));
      return;
    }

    this.getCountOfFilesOrFolderInDraggedContent(content);
    content.forEach(item => this.contents.push(item));
    this.contentSelected();
  };

  private setErrorMessage = (error: string) => {
    this.errorMessage = error;
    this.errorMessageEvent.emit(error);
  };

  private contentSelected = () => {
    this.hasContentSelected = false;
    this.uploadContentToolTip = '';
    this.pipeContent = '';
    this.selectedContentName = '';
    this.setErrorMessage('');
    this.hasMultipleFilesUpload = false;
    this.iconOfSelectedContent = '';

    if (this.countOfFoldersUploaded == 1 && this.countOfFilesUploaded == 0) {
      this.hasContentSelected = true;
      this.selectedContentName = `${this.contents[0].path.split('/')[0]}`;
      this.hasFolderContentUploaded = true;
      this.pipeContent = this.saI18nPipe.transform(
        'file-manager-upload-content-dialog-pipe-files-selected',
        this.contents.length
      );
      this.iconOfSelectedContent = 'hd-icon-folder';
    } else if (this.countOfFoldersUploaded > 1 && this.countOfFilesUploaded == 0) {
      this.hasContentSelected = true;
      this.selectedContentName = this.saI18nPipe.transform(
        'file-manager-upload-content-dialog-multiple-folders-selected'
      );
      this.hasFolderContentUploaded = true;
      this.pipeContent = this.saI18nPipe.transform(
        'file-manager-upload-content-dialog-pipe-files-selected',
        this.contents.length
      );
      this.iconOfSelectedContent = 'hd-icon-folder';
    }
    if (this.countOfFilesUploaded == 1 && this.countOfFoldersUploaded == 0) {
      this.hasContentSelected = true;
      this.selectedContentName = `${this.contents[0].content.name}`;
      this.hasFolderContentUploaded = false;
      this.iconOfSelectedContent = 'hd-icon-filetype-document';
    } else if (this.countOfFilesUploaded > 1 && this.countOfFoldersUploaded == 0) {
      this.hasContentSelected = true;
      this.selectedContentName = this.saI18nPipe.transform(
        'file-manager-upload-content-dialog-multiple-files-selected',
        this.contents.length
      );
      this.hasFolderContentUploaded = false;
      this.hasMultipleFilesUpload = true;
      this.iconOfSelectedContent = 'hd-icon-filetype-document';
    }
    if (this.countOfFilesUploaded >= 1 && this.countOfFoldersUploaded >= 1) {
      this.hasContentSelected = true;
      this.hasFolderContentUploaded = true;
      this.selectedContentName = this.saI18nPipe.transform(
        'file-manager-upload-content-dialog-multiple-items-selected'
      );
      this.pipeContent = this.saI18nPipe.transform(
        'file-manager-upload-content-dialog-pipe-files-selected',
        this.contents.length
      );
      this.iconOfSelectedContent = 'hd-icon-folder-files';
    }
    this.getToolTipOfContents();
    this.uploadEvent.emit({ content: this.contents, reason: null });
  };

  private getCountOfFilesOrFolderUploaded = (uploadContent: File[]): void => {
    if (uploadContent && uploadContent.length > 0 && uploadContent[0]['webkitRelativePath'] != '') {
      this.countOfFoldersUploaded++;
    } else {
      this.countOfFilesUploaded += uploadContent.length;
    }
  };

  private getCountOfFilesOrFolderInDraggedContent = (uploadContent: UploadContent[]): void => {
    const parentFolders = new Map();
    uploadContent.forEach(content => {
      const parentFolder = content.path.split('/')[0];
      if (parentFolder !== '') {
        parentFolders.set(parentFolder, parentFolder);
      }
    });
    this.countOfFoldersUploaded += parentFolders.size;
    this.countOfFilesUploaded += uploadContent.filter(x => x.path === '').length;
  };

  private getToolTipOfContents = (): void => {
    this.contents.forEach(filename => {
      this.uploadContentToolTip +=
        filename.path.length == 0 ? filename.content.name.concat(' \n ') : filename.path.concat(' \n ');
    });
  };

  private addToContents(files: File[]) {
    files.forEach(content => {
      this.contents.push({
        content: content,
        path: content['webkitRelativePath'],
      } as UploadContent);
    });
  }

  private stopEventBubble(event: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  private updateValidators(violated: boolean, file: File, reason?: FileCheckType, previewMode: boolean = false): void {
    this.file = file;
    this.violated = violated;
    this.hasContentSelected = !!file;

    const uploadContent = {
      content: file,
      path: null,
    } as UploadContent;

    if (!previewMode) {
      this.uploadEvent.emit({ content: [uploadContent], reason: reason });
    }
  }

  private getFileExtension(file: File): string {
    if (file === null || file === undefined) {
      return '';
    }

    return file.name.substring(file.name.lastIndexOf('.'), file.name.length).toLowerCase();
  }

  private fileCleanUpOperation(): void {
    this.updateValidators(false, null);
    this.stopEventBubble(event);
    if (this.readyToUploadState) {
      this.customDragAndDropMessage = this.fileUploadMessage;
    }
  }

  private cancelFileUpload(): void {
    this.fileUploadCancelEvent.emit();
    this.fileCleanUpOperation();
  }
}
